// Diagnostic assessment questions for skill evaluation

export type QuestionType = "solve" | "identify-technique" | "estimate";
export type SkillLevel = "novice" | "developing" | "proficient" | "advanced";

export interface AssessmentQuestion {
  id: string;
  type: QuestionType;
  problem: string;
  correctAnswer: string;
  options: string[];
  timeLimitSeconds: number;
  skillsTested: string[];
  difficulty: "easy" | "medium" | "hard";
}

export interface AssessmentResponse {
  questionId: string;
  userAnswer: string | null;
  isCorrect: boolean;
  timeTakenMs: number;
}

export interface SkillScore {
  skillId: string;
  skillName: string;
  level: SkillLevel;
  score: number;
  correct: number;
  total: number;
}

export interface AssessmentResult {
  totalQuestions: number;
  correctCount: number;
  accuracyPercent: number;
  avgTimeMs: number;
  skillScores: SkillScore[];
}

// Skill definitions with display names and categories
export const SKILL_DEFINITIONS: Record<
  string,
  { name: string; category: string }
> = {
  "number-sense": { name: "Number Sense", category: "Foundation" },
  addition: { name: "Addition", category: "Foundation" },
  subtraction: { name: "Subtraction", category: "Foundation" },
  multiplication: { name: "Multiplication", category: "Core" },
  division: { name: "Division", category: "Core" },
  tables: { name: "Times Tables", category: "Core" },
  "mental-tricks": { name: "Mental Tricks", category: "Advanced" },
  "vedic-math": { name: "Vedic Math", category: "Advanced" },
  "vedic-base": { name: "Base Method", category: "Advanced" },
  squaring: { name: "Squaring", category: "Advanced" },
  "multiply-11": { name: "Multiply by 11", category: "Tricks" },
  divisibility: { name: "Divisibility", category: "Core" },
  patterns: { name: "Patterns", category: "Problem Solving" },
  sequences: { name: "Sequences", category: "Problem Solving" },
  estimation: { name: "Estimation", category: "Foundation" },
};

// Diagnostic questions covering all skill areas
export const DIAGNOSTIC_QUESTIONS: AssessmentQuestion[] = [
  // Number Sense & Addition (3 questions)
  {
    id: "ns-1",
    type: "solve",
    problem: "48 + 37",
    correctAnswer: "85",
    options: ["75", "85", "95", "87"],
    timeLimitSeconds: 8,
    skillsTested: ["number-sense", "addition"],
    difficulty: "easy",
  },
  {
    id: "ns-2",
    type: "solve",
    problem: "156 + 289",
    correctAnswer: "445",
    options: ["435", "445", "455", "425"],
    timeLimitSeconds: 12,
    skillsTested: ["number-sense", "addition"],
    difficulty: "medium",
  },
  {
    id: "ns-3",
    type: "solve",
    problem: "999 + 456",
    correctAnswer: "1455",
    options: ["1455", "1445", "1465", "1555"],
    timeLimitSeconds: 10,
    skillsTested: ["number-sense", "addition", "mental-tricks"],
    difficulty: "medium",
  },

  // Subtraction (2 questions)
  {
    id: "sub-1",
    type: "solve",
    problem: "156 - 89",
    correctAnswer: "67",
    options: ["57", "67", "77", "63"],
    timeLimitSeconds: 12,
    skillsTested: ["number-sense", "subtraction"],
    difficulty: "medium",
  },
  {
    id: "sub-2",
    type: "solve",
    problem: "1000 - 347",
    correctAnswer: "653",
    options: ["643", "653", "663", "753"],
    timeLimitSeconds: 15,
    skillsTested: ["subtraction", "mental-tricks"],
    difficulty: "medium",
  },

  // Multiplication - Tables (2 questions)
  {
    id: "mult-1",
    type: "solve",
    problem: "7 × 8",
    correctAnswer: "56",
    options: ["48", "54", "56", "64"],
    timeLimitSeconds: 5,
    skillsTested: ["multiplication", "tables"],
    difficulty: "easy",
  },
  {
    id: "mult-2",
    type: "solve",
    problem: "12 × 9",
    correctAnswer: "108",
    options: ["98", "108", "118", "99"],
    timeLimitSeconds: 8,
    skillsTested: ["multiplication", "tables"],
    difficulty: "easy",
  },

  // Multiplication - Mental Tricks (2 questions)
  {
    id: "mult-3",
    type: "solve",
    problem: "25 × 16",
    correctAnswer: "400",
    options: ["350", "400", "425", "450"],
    timeLimitSeconds: 15,
    skillsTested: ["multiplication", "mental-tricks"],
    difficulty: "medium",
  },
  {
    id: "mult-4",
    type: "solve",
    problem: "47 × 11",
    correctAnswer: "517",
    options: ["497", "507", "517", "527"],
    timeLimitSeconds: 10,
    skillsTested: ["multiplication", "multiply-11", "vedic-math"],
    difficulty: "easy",
  },

  // Vedic Math (2 questions)
  {
    id: "vedic-1",
    type: "solve",
    problem: "35²",
    correctAnswer: "1225",
    options: ["1125", "1225", "1325", "1250"],
    timeLimitSeconds: 15,
    skillsTested: ["vedic-math", "squaring"],
    difficulty: "medium",
  },
  {
    id: "vedic-2",
    type: "solve",
    problem: "98 × 97",
    correctAnswer: "9506",
    options: ["9406", "9506", "9516", "9606"],
    timeLimitSeconds: 20,
    skillsTested: ["multiplication", "vedic-math", "vedic-base"],
    difficulty: "hard",
  },

  // Division (2 questions)
  {
    id: "div-1",
    type: "solve",
    problem: "144 ÷ 12",
    correctAnswer: "12",
    options: ["10", "11", "12", "14"],
    timeLimitSeconds: 10,
    skillsTested: ["division", "tables"],
    difficulty: "easy",
  },
  {
    id: "div-2",
    type: "solve",
    problem: "Is 738 divisible by 9?",
    correctAnswer: "Yes",
    options: ["Yes", "No"],
    timeLimitSeconds: 12,
    skillsTested: ["division", "divisibility"],
    difficulty: "medium",
  },

  // Pattern Recognition (2 questions)
  {
    id: "pattern-1",
    type: "solve",
    problem: "Next in sequence: 2, 6, 18, 54, ?",
    correctAnswer: "162",
    options: ["108", "162", "180", "216"],
    timeLimitSeconds: 20,
    skillsTested: ["patterns", "sequences"],
    difficulty: "medium",
  },
  {
    id: "pattern-2",
    type: "solve",
    problem: "Next in sequence: 1, 4, 9, 16, 25, ?",
    correctAnswer: "36",
    options: ["30", "34", "36", "49"],
    timeLimitSeconds: 15,
    skillsTested: ["patterns", "squaring"],
    difficulty: "easy",
  },

  // Estimation (1 question)
  {
    id: "est-1",
    type: "estimate",
    problem: "Approximately: 49 × 51",
    correctAnswer: "2499",
    options: ["2400", "2499", "2500", "2600"],
    timeLimitSeconds: 10,
    skillsTested: ["estimation", "multiplication"],
    difficulty: "medium",
  },
];

// Utility functions
export function shuffleQuestions(
  questions: AssessmentQuestion[],
): AssessmentQuestion[] {
  const shuffled = [...questions];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export function shuffleOptions(
  question: AssessmentQuestion,
): AssessmentQuestion {
  const shuffledOptions = [...question.options];
  for (let i = shuffledOptions.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffledOptions[i], shuffledOptions[j]] = [
      shuffledOptions[j],
      shuffledOptions[i],
    ];
  }
  return { ...question, options: shuffledOptions };
}

export function calculateSkillLevel(score: number): SkillLevel {
  if (score >= 90) return "advanced";
  if (score >= 70) return "proficient";
  if (score >= 50) return "developing";
  return "novice";
}

export function calculateAssessmentResult(
  questions: AssessmentQuestion[],
  responses: AssessmentResponse[],
): AssessmentResult {
  const correctCount = responses.filter((r) => r.isCorrect).length;
  const totalTime = responses.reduce((sum, r) => sum + r.timeTakenMs, 0);

  // Calculate skill scores
  const skillStats: Record<string, { correct: number; total: number }> = {};

  questions.forEach((q, idx) => {
    const response = responses[idx];
    q.skillsTested.forEach((skill) => {
      if (!skillStats[skill]) {
        skillStats[skill] = { correct: 0, total: 0 };
      }
      skillStats[skill].total++;
      if (response?.isCorrect) {
        skillStats[skill].correct++;
      }
    });
  });

  const skillScores: SkillScore[] = Object.entries(skillStats).map(
    ([skillId, stats]) => {
      const score = Math.round((stats.correct / stats.total) * 100);
      return {
        skillId,
        skillName: SKILL_DEFINITIONS[skillId]?.name ?? skillId,
        level: calculateSkillLevel(score),
        score,
        correct: stats.correct,
        total: stats.total,
      };
    },
  );

  // Sort by score ascending (weakest first)
  skillScores.sort((a, b) => a.score - b.score);

  return {
    totalQuestions: questions.length,
    correctCount,
    accuracyPercent: Math.round((correctCount / questions.length) * 100),
    avgTimeMs: Math.round(totalTime / responses.length),
    skillScores,
  };
}
